import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Panel, Empty, useFlash, readForm } from '../../ui/kit.js';
import { day, printDate, nextId } from '../../state/building.js';

import GR86 from '../../media/images/car-gr86.jpg';
import CaymanGT4RS from '../../media/images/car-gt4rs.jpg';
import Emira from '../../media/images/car-emira.webp';

const TAG_TONE = { 'Active': 'ok', 'Renewal due': 'warn', 'Renewal sent': 'info' };

const VEHICLES = [
    { id: 1, plate: 'GRX 8614', make: 'Toyota GR86', color: 'Neptune', space: '2-14', tag: 'Active', decal: 'D-40981', image: GR86, focus: '50% 55%' },
    { id: 2, plate: 'RS4 1204', make: 'Porsche 718 Cayman GT4 RS', color: 'Arctic Grey', space: '2-15', tag: 'Active', decal: 'D-40982', image: CaymanGT4RS, focus: '45% 60%' },
    { id: 3, plate: 'EMR 216L', make: 'Lotus Emira', color: 'Green', space: '2-16', tag: 'Renewal due', decal: 'D-40983', image: Emira, focus: '40% 55%' }
];

const PASSES = [
    { id: 1, plate: 'FLA 7Q2', guest: 'Jordan Blake', from: day(0), nights: 1, pass: 'G-2291' }
];

/* Vehicle Registration: the resident's cars and guest parking passes. */
function VehiclesScreen() {
    const [flash, notify] = useFlash();
    const [vehicles, setVehicles] = useState(VEHICLES);
    const [passes, setPasses] = useState(PASSES);

    const renew = vehicle => {
        setVehicles(list => list.map(v => (v.id === vehicle.id ? { ...v, tag: 'Renewal sent' } : v)));
        notify(`Renewal requested for ${vehicle.plate}. Pick up the new decal at the front desk.`);
    };

    const submit = event => {
        const values = readForm(event, ['plate']);
        if (!values) return notify('Enter the guest plate number first.');
        if (passes.length >= 2) return notify('Each unit can hold two active guest passes at a time.');

        const pass = `G-${2300 + passes.length + Math.floor(Math.random() * 90)}`;
        setPasses(list => [...list, {
            id: nextId(),
            plate: values.plate.toUpperCase(),
            guest: values.guest,
            from: printDate(values.date),
            nights: Number(values.nights),
            pass
        }]);
        notify(`Pass ${pass} issued for ${values.plate.toUpperCase()}. Valet has it on file.`);
    };

    const end = p => {
        setPasses(list => list.filter(x => x.id !== p.id));
        notify(`Guest pass ${p.pass} ended.`);
    };

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Vehicles" title="Registered vehicles" />
            {flash}

            <ul className="rows">
                {vehicles.map(v => (
                    <li className="row row--thumb" key={v.id}>
                        <img className="row__thumb" src={v.image} alt={`${v.color} ${v.make}`} loading="lazy" style={{ objectPosition: v.focus }} />
                        <div className="row__main">
                            <span className="row__title">{v.make} · {v.plate}</span>
                            <span className="row__meta">{v.color} · space {v.space} · decal {v.decal}</span>
                        </div>
                        <div className="row__end">
                            <Chip tone={TAG_TONE[v.tag]}>{v.tag}</Chip>
                            {v.tag === 'Renewal due' && (
                                <button type="button" className="btn btn-outline-light btn-sm" onClick={() => renew(v)}>Renew decal</button>
                            )}
                        </div>
                    </li>
                ))}
            </ul>

            <div className="split section-gap">
                <Panel as="form" className="panel--accent" onSubmit={submit}>
                    <h4>Guest parking pass</h4>
                    <div className="form">
                        <Field label="Plate number">
                            <input name="plate" className="form-control" placeholder="e.g. BGT 4410" />
                        </Field>
                        <Field label="Guest name">
                            <input name="guest" className="form-control" placeholder="Optional" />
                        </Field>
                        <Field label="Arriving">
                            <input name="date" type="date" className="form-control" />
                        </Field>
                        <Field label="Nights">
                            <select name="nights" className="form-control" defaultValue="1">
                                {[1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </Field>
                        <button type="submit" className="btn btn-primary">Issue pass</button>
                    </div>
                </Panel>

                <div className="stack">
                    <h4>Active guest passes</h4>
                    {passes.length === 0 ? <Empty>No active guest passes.</Empty> : (
                        <ul className="rows">
                            {passes.map(p => (
                                <li className="row" key={p.id}>
                                    <div className="row__main">
                                        <span className="row__title">{p.plate}{p.guest ? ` · ${p.guest}` : ''}</span>
                                        <span className="row__meta">Pass {p.pass} · from {p.from} · {p.nights} {p.nights === 1 ? 'night' : 'nights'} · guest level P1</span>
                                    </div>
                                    <div className="row__end">
                                        <Chip tone="ok">Active</Chip>
                                        <button type="button" className="btn btn-outline-light btn-sm" onClick={() => end(p)}>End pass</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

export default VehiclesScreen;
